// Live camera: Camera2 → MediaCodec H.264 on the phone, relayed as
// VideoFrames, decoded here with WebCodecs. The phone's camera privacy
// indicator stays on while streaming (AGENTS.md §7.1).

import type { CameraDescriptor } from "@fistos/shared";
import { Camera, CameraOff, Info, KeyRound, LoaderCircle } from "lucide-solid";
import { createSignal, onCleanup, Show } from "solid-js";
import { toast } from "somoto";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectPortal, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MediaSession, webCodecsSupported, type MediaStats } from "@/lib/media";
import { actions } from "@/lib/proto";
import { OfflineNote, run, Unavailable, type PanelProps } from "./common";

const SIZES = [
  { label: "640 × 480", w: 640, h: 480, bitrate: 1_000_000 },
  { label: "1280 × 720", w: 1280, h: 720, bitrate: 2_500_000 },
  { label: "1920 × 1080", w: 1920, h: 1080, bitrate: 5_000_000 },
];

export function CameraPanel(props: PanelProps) {
  const id = () => props.device.id;
  const cameras = (): CameraDescriptor[] => props.caps?.cameras ?? [];
  const [camera, setCamera] = createSignal<CameraDescriptor | undefined>(cameras()[0]);
  const [size, setSize] = createSignal(SIZES[1]);
  const [state, setState] = createSignal<"idle" | "starting" | "live">("idle");
  const [stats, setStats] = createSignal<MediaStats | null>(null);
  let canvas!: HTMLCanvasElement;
  let media: MediaSession | null = null;

  const start = async () => {
    if (!webCodecsSupported()) return toast.error("This browser cannot decode H.264 (WebCodecs).");
    setState("starting");
    setStats(null);
    media = new MediaSession(id(), { canvas, onStats: setStats, onError: (m) => toast.error(m) });
    media.start();
    const s = size();
    const r = await run(
      id(),
      actions.media({
        case: "cameraStart",
        value: {
          $typeName: "fistos.v1.CameraStart",
          cameraId: camera()?.cameraId ?? "",
          width: s.w,
          height: s.h,
          fps: 30,
          bitrateBps: s.bitrate,
        },
      }),
    );
    if (!r) {
      media.stop();
      media = null;
      setState("idle");
      return;
    }
    setState("live");
  };
  const stop = async () => {
    media?.stop();
    media = null;
    setState("idle");
    await run(id(), actions.media({ case: "cameraStop", value: { $typeName: "fistos.v1.CameraStop" } }));
  };
  const keyframe = () =>
    run(id(), actions.media({ case: "requestKeyframe", value: { $typeName: "fistos.v1.RequestKeyframe" } }));
  onCleanup(() => {
    if (state() !== "idle") void stop();
  });

  return (
    <Show
      when={props.caps?.camera !== false}
      fallback={<Unavailable title="No camera">This phone reports no usable camera.</Unavailable>}
    >
      <div class="grid gap-4">
        <Show when={!props.device.online}>
          <OfflineNote device={props.device} />
        </Show>
        <div class="flex flex-wrap items-end gap-2">
          <Select<CameraDescriptor>
            options={cameras()}
            optionValue="cameraId"
            optionTextValue="cameraId"
            value={camera()}
            onChange={(c) => c && setCamera(c)}
            disabled={state() !== "idle"}
            placeholder="Default camera"
            itemComponent={(p) => (
              <SelectItem item={p.item}>
                {p.item.rawValue.facing || "camera"} · {p.item.rawValue.cameraId}
              </SelectItem>
            )}
          >
            <SelectTrigger class="w-44">
              <SelectValue<CameraDescriptor>>
                {(s) => `${s.selectedOption()?.facing || "camera"} · ${s.selectedOption()?.cameraId}`}
              </SelectValue>
            </SelectTrigger>
            <SelectPortal>
              <SelectContent />
            </SelectPortal>
          </Select>
          <Select<(typeof SIZES)[number]>
            options={SIZES}
            optionValue="label"
            optionTextValue="label"
            value={size()}
            onChange={(s) => s && setSize(s)}
            disabled={state() !== "idle"}
            itemComponent={(p) => <SelectItem item={p.item}>{p.item.rawValue.label}</SelectItem>}
          >
            <SelectTrigger class="w-36">
              <SelectValue<(typeof SIZES)[number]>>{(s) => s.selectedOption()?.label}</SelectValue>
            </SelectTrigger>
            <SelectPortal>
              <SelectContent />
            </SelectPortal>
          </Select>
          <Show
            when={state() === "idle"}
            fallback={
              <>
                <Button variant="outline" onClick={stop}>
                  <CameraOff /> Stop
                </Button>
                <Button variant="ghost" onClick={keyframe} disabled={state() !== "live"}>
                  <KeyRound /> Keyframe
                </Button>
              </>
            }
          >
            <Button onClick={start} disabled={!props.device.online}>
              <Camera /> Start camera
            </Button>
          </Show>
        </div>
        <Card class="overflow-hidden p-0">
          <CardContent class="relative flex aspect-video items-center justify-center bg-black p-0">
            <canvas ref={canvas} class="max-h-full max-w-full" classList={{ hidden: !stats()?.frames }} />
            <Show when={!stats()?.frames}>
              <div class="absolute inset-0 flex flex-col items-center justify-center gap-2 text-sm text-white/60">
                <Show
                  when={state() === "idle"}
                  fallback={
                    <>
                      <LoaderCircle class="size-6 animate-spin" /> Waiting for video…
                    </>
                  }
                >
                  <Camera class="size-6" /> Camera off
                </Show>
              </div>
            </Show>
            <Show when={stats()?.frames}>
              <div class="absolute top-2 left-2 flex items-center gap-1.5 rounded bg-black/60 px-2 py-0.5 text-[11px] text-white tabular-nums">
                <span class="size-1.5 animate-pulse rounded-full bg-red-500" />
                {stats()!.width}×{stats()!.height}
              </div>
            </Show>
          </CardContent>
        </Card>
        <p class="text-muted-foreground flex items-center gap-1.5 text-xs">
          <Info class="size-3.5" /> The phone shows its camera privacy indicator while streaming. Starting the camera
          may need your approval.
        </p>
      </div>
    </Show>
  );
}
